import { useAuthenticator } from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import { Button, FormControl, Tab, Table, Tabs } from "react-bootstrap";
import { DeviceOrder, DeviceOrderStatus } from "stubminer-logic/dist";
import { client } from "../App";
import { useProfileContext } from "../contexts/ProfileContexts";
import { updateDeviceOrder } from "../graphql/mutations";
import { getDeviceOrders } from "../graphql/queries";
import { refreshMe } from "../utils";

interface GetDeviceOrdersReturn {
  data: {
    getDeviceOrders: DeviceOrder[];
  };
}

const DeviceOrders = () => {
  const [loading, setLoading] = useState(false);
  const [deviceOrders, setDeviceOrders] = useState<DeviceOrder[]>([]);
  const [status, setStatus] = useState<DeviceOrderStatus>("pending");
  const useAuthenticatorReturn = useAuthenticator();

  const cognitoUser = useAuthenticatorReturn.user;
  const { setProfile, setUser, user } = useProfileContext();

  useEffect(() => {
    console.log("cognito user", cognitoUser);
    if (cognitoUser && !user) {
      refreshMe(setUser, setProfile);
    }
  }, [cognitoUser, setProfile, setUser]);

  const fetchDeviceOrders = async (status: DeviceOrderStatus) => {
    if (user) {
      setLoading(true);
      try {
        const response = (await client.graphql({
          query: getDeviceOrders,
          variables: {
            getDeviceOrdersInput: { status },
          },
        })) as GetDeviceOrdersReturn;
        const newDeviceOrders = response.data.getDeviceOrders;
        // this is in order to keep it from being empty and triggering infinite requests
        newDeviceOrders.push({
          notes: "",
          status,
          sub: "",
          timestamp: "",
          name: "Placeholder (don't edit this one)",
        });
        setDeviceOrders(newDeviceOrders);
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const filteredDeviceOrders = deviceOrders.filter(
      (deviceOrder) => deviceOrder.status === status
    );
    if (user && user.isAdmin && filteredDeviceOrders.length === 0) {
      fetchDeviceOrders(status);
    }
  }, [user, deviceOrders, status]);

  if (!user || !user.isAdmin) {
    return <div>Unauthorized</div>;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  const handleUpdateDeviceOrderClick = async (
    index: number,
    status: DeviceOrderStatus,
    notes: string = " ",
    trackingNumber: string = " "
  ) => {
    const deviceOrder = deviceOrders[index];
    try {
      await client.graphql({
        query: updateDeviceOrder,
        variables: {
          updateDeviceOrderInput: {
            status,
            trackingNumber: trackingNumber || " ",
            notes,
            customerSub: deviceOrder.sub,
            timestamp: deviceOrder.timestamp,
          },
        },
      });
      fetchDeviceOrders(status);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div>
      <h1>Device Orders</h1>
      <Tabs
        activeKey={status}
        onSelect={(k) => setStatus(k as DeviceOrderStatus)}
      >
        <Tab eventKey="pending" title="Pending"></Tab>
        <Tab eventKey="in-progress" title="In Progress"></Tab>
        <Tab eventKey="mailed" title="Mailed"></Tab>
        <Tab eventKey="completed" title="Completed"></Tab>
      </Tabs>
      <Table>
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Email</th>
            <th>Name</th>
            <th>Address Line 1</th>
            <th>Line 2</th>
            <th>City</th>
            <th>State</th>
            <th>Postal Code</th>
            <th>Country</th>
            <th>Status</th>
            <th>Tracking Number</th>
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {deviceOrders.map((deviceOrder, index) => (
            <tr key={deviceOrder.timestamp}>
              <td>{deviceOrder.timestamp}</td>
              <td>{deviceOrder.email}</td>
              <td>{deviceOrder.name}</td>
              <td>{deviceOrder.line1}</td>
              <td>{deviceOrder.line2}</td>
              <td>{deviceOrder.city}</td>
              <td>{deviceOrder.state}</td>
              <td>{deviceOrder.postal_code}</td>
              <td>{deviceOrder.country}</td>
              <td>
                <FormControl
                  as="select"
                  value={deviceOrder.status}
                  onChange={(e) => {
                    setDeviceOrders((prev) => {
                      const newDeviceOrders = [...prev];
                      newDeviceOrders[index].status = e.target
                        .value as DeviceOrderStatus;
                      return newDeviceOrders;
                    });
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="mailed">Mailed</option>
                  <option value="completed">Completed</option>
                </FormControl>
              </td>
              <td>
                <FormControl
                  type="text"
                  value={deviceOrder.trackingNumber}
                  onChange={(e) => {
                    setDeviceOrders((prev) => {
                      const newDeviceOrders = [...prev];
                      newDeviceOrders[index].trackingNumber = e.target.value;
                      return newDeviceOrders;
                    });
                  }}
                />
              </td>
              <td>
                <FormControl
                  type="text"
                  as="textarea"
                  value={deviceOrder.notes}
                  onChange={(e) => {
                    setDeviceOrders((prev) => {
                      const newDeviceOrders = [...prev];
                      newDeviceOrders[index].notes = e.target.value;
                      return newDeviceOrders;
                    });
                  }}
                />
              </td>
              <td>
                <Button
                  onClick={() =>
                    handleUpdateDeviceOrderClick(
                      index,
                      deviceOrder.status,
                      deviceOrder.notes,
                      deviceOrder.trackingNumber
                    )
                  }
                >
                  Update
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default DeviceOrders;
